import React, { useState } from 'react';
import { MultiSelectList } from 'src/domains/common/components/multi-select-list/multi-select-list';
import { observer } from 'src/utils/mobx-react';
import { GamePickerState } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/casino-sections/CasinoGamesPicker.state';
import { makeAutoObservable } from 'mobx';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import { TreeType } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/sportsbook-sections/sports-picker/utils';

class CasinoGamesViewerState {
    public readonly gamePickerState: GamePickerState;

    public constructor(
        private readonly campaignModel: CampaignModel,
        private readonly type: GamesViewerType
    ) {
        this.gamePickerState = new GamePickerState($appState.appCasinoState.gameStore, 'casino');
        this.setSelectedGames();
        this.gamePickerState.expandAllNodes();
        this.gamePickerState.setReadOnlyMode(true);

        makeAutoObservable(this);
    }

    public get gamesTreeForView(): Array<TreeType> {
        const filterTreeLeaves = (tree: TreeType[], condition: (node: TreeType) => boolean): Array<TreeType> => {
            return tree
                .map((node) => {
                    if (node.children !== undefined && node.children.length > 0) {
                        const filteredChildren = filterTreeLeaves(node.children, condition);

                        if (filteredChildren.length > 0) {
                            return {
                                ...node,
                                children: filteredChildren,
                            };
                        } else {
                            return null;
                        }
                    } else {
                        return condition(node) ? node : null;
                    }
                })
                .filter((node): node is TreeType => node !== null);
        };
        return filterTreeLeaves(this.gamePickerState.filteredNodes, (node) =>
            this.gamePickerState.checked.includes(node.value)
        );
    }

    private setSelectedGames = (): void => {
        switch (this.type) {
            case 'action-conditions': {
                const criteria = this.campaignModel.actionConditions?.criteria;

                if (criteria === null || criteria === undefined) {
                    return;
                }

                for (const item of criteria) {
                    if (item.type === 'casino-game' && Array.isArray(item.value)) {
                        this.gamePickerState.checked = [...item.value];
                    }
                }
                return;
            }
            case 'playthrough-games': {
                const playthroughEligibleGames = this.campaignModel.bonusRewards?.[0]?.playthroughEligibleGames;
                if (Array.isArray(playthroughEligibleGames)) {
                    this.gamePickerState.checked = playthroughEligibleGames;
                }
                return;
            }
            case 'eligible-games': {
                const usageConditions = this.campaignModel.bonusRewards?.[0]?.usageConditions?.[0];
                if (usageConditions?.type === 'casino-game') {
                    if (Array.isArray(usageConditions.value)) {
                        this.gamePickerState.checked = usageConditions.value;
                    }
                }
                return;
            }
        }
    };
}

type GamesViewerType = 'action-conditions' | 'eligible-games' | 'playthrough-games';

interface CasinoGamesViewerPropsType {
    campaignModel: CampaignModel;
    type: GamesViewerType;
}

export const CasinoGamesViewer = observer(
    'CasinoGamesViewer',
    ({ campaignModel, type }: CasinoGamesViewerPropsType): JSX.Element | null => {
        const [state] = useState(() => new CasinoGamesViewerState(campaignModel, type));

        const {
            showAllBadges,
            expanded,
            visibleChecked,
            keywordInputState,
            checked,
            toggleShowAllBadges,
            handleCheck,
            handleExpand,
            setKeyword,
            handleRemoveChecked,
        } = state.gamePickerState;

        return (
            <MultiSelectList
                filteredNodes={state.gamesTreeForView}
                showAllBadges={showAllBadges}
                checked={checked}
                visibleChecked={visibleChecked}
                keywordInputState={keywordInputState}
                expanded={expanded}
                visibleCheckedWithLabels={[]}
                toggleShowAllBadges={toggleShowAllBadges}
                handleCheck={handleCheck}
                handleExpand={handleExpand}
                setKeyword={setKeyword}
                handleRemoveChecked={handleRemoveChecked}
                remaining={visibleChecked.remaining}
                remainingLabel='Games'
            />
        );
    }
);
